/* AWA Evaporative Product Card: selectable installation and controller options.
   Quote-only: no add-to-cart, price promises or booking mutations from this component. */
(() => {
  if (customElements.get('awa-evap-product-card')) return;

  class AWAEvapProductCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-awa-evap-data]')?.textContent || '{}');
      } catch (_) { this.config = {}; }

      this.install = [...this.querySelectorAll('[data-awa-evap-install]')];
      this.controllers = [...this.querySelectorAll('[data-awa-evap-controller]')];
      this.postcode = this.querySelector('[data-awa-evap-postcode]');
      this.postcodeForm = this.querySelector('[data-awa-evap-postcode-form]');
      this.postcodeResult = this.querySelector('[data-awa-evap-postcode-result]');
      this.summaryInstall = this.querySelector('[data-awa-evap-summary-install]');
      this.summaryController = this.querySelector('[data-awa-evap-summary-controller]');
      this.quote = this.querySelector('[data-awa-product-quote-open]');
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-awa-evap-install], [data-awa-evap-controller]')) this.render();
      }, { signal });
      this.postcode?.addEventListener('input', () => {
        if (this.postcodeResult) {
          this.postcodeResult.dataset.state = 'neutral';
          this.postcodeResult.textContent = this.config.rules
            ? 'Check your postcode to review the current enquiry area.'
            : 'Installation coverage is confirmed by AWA when you request your quote.';
        }
        this.render();
      }, { signal });
      this.postcodeForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.checkPostcode();
      }, { signal });
      this.render();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.initialized = false;
    }

    choice(inputs, fallback) {
      return inputs.find(input => input.checked)?.value || fallback;
    }

    money(value) {
      const cents = Number(value);
      if (!Number.isFinite(cents) || cents < 0) return '';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: 'AUD'
      }).format(cents / 100);
    }

    render() {
      const mode = this.choice(this.install, 'supply');
      const controller = this.choice(this.controllers, 'standard');
      const swap = this.config.swap || {};
      const dropper = this.config.roofDropper || {};
      const upgrade = this.config.controllerUpgrade || {};
      const standard = this.config.standardController || 'Standard controller (included)';
      const labels = {
        supply: 'Supply Only',
        swap: 'Like-for-like swap',
        dropper: 'Like-for-like swap + roof dropper'
      };
      const modeLabel = labels[mode] || labels.supply;
      let installationSummary = modeLabel;
      if (mode !== 'supply') {
        installationSummary += swap.available
          ? ' — swap service ' + this.money(swap.price)
          : ' — swap service price to confirm';
        if (mode === 'dropper') {
          installationSummary += dropper.available
            ? ' + roof dropper ' + this.money(dropper.price)
            : ' + roof dropper price to confirm';
        }
      }
      const controllerLabel = controller === 'upgrade'
        ? 'Controller upgrade: ' + (upgrade.title || 'requested') + ' (compatibility and upgrade price to confirm)'
        : standard;
      if (this.summaryInstall) this.summaryInstall.textContent = installationSummary;
      if (this.summaryController) this.summaryController.textContent = controllerLabel;

      if (this.quote) {
        this.quote.dataset.quotePreselect = mode === 'supply' ? 'Supply Only' : 'Supply + Installation';
        this.quote.dataset.installationPostcode = String(this.postcode?.value || '').trim();
        this.quote.dataset.evapInstallationOption = modeLabel;
        this.quote.dataset.evapInstallationService = mode === 'supply' ? '' :
          (swap.title ? swap.title + (swap.available ? ' — ' + this.money(swap.price) : '') : 'Service price to confirm');
        this.quote.dataset.evapRoofDropper = mode === 'dropper'
          ? (dropper.title ? dropper.title + (dropper.available ? ' — ' + this.money(dropper.price) : '') : 'Roof dropper addition — quote required')
          : '';
        this.quote.dataset.evapController = controllerLabel;
        this.quote.dataset.evapControllerCatalogPrice = controller === 'upgrade' && upgrade.available
          ? 'Standalone controller catalogue price ' + this.money(upgrade.price) + '; final upgrade amount quoted'
          : '';
      }
    }

    showPostcodeResult(message, status = 'neutral') {
      if (!this.postcodeResult) return;
      this.postcodeResult.textContent = message;
      this.postcodeResult.dataset.state = status;
    }

    checkPostcode() {
      const postcode = String(this.postcode?.value || '').trim();
      const engine = window.AWAInstallationCoverage;
      if (!/^[0-9]{4}$/.test(postcode) || !engine?.isAustralianPostcode?.(postcode)) {
        this.showPostcodeResult('Enter a valid 4-digit Australian postcode.', 'invalid');
        return;
      }
      if (!this.config.rules) {
        this.showPostcodeResult('Coverage for ' + postcode + ' will be confirmed by AWA when you request an installation quote.', 'pending');
        return;
      }
      if (!engine?.parseRules || !engine?.evaluate) {
        this.showPostcodeResult('Unable to check coverage. Please request a quote.', 'pending');
        return;
      }
      const parsed = engine.parseRules(this.config.rules);
      if (!parsed.valid) {
        this.showPostcodeResult('Postcode rules need review. Our team will confirm service availability.', 'pending');
        return;
      }
      const result = engine.evaluate({enabled:true,parsed,rules:this.config.rules}, postcode);
      if (result.available) {
        this.showPostcodeResult(postcode + ' is in the configured installation enquiry area. This is not an installation booking or final quote.', 'available');
      } else if (result.available === false) {
        this.showPostcodeResult(postcode + ' is outside the currently configured installation area. Please enquire for other options.', 'unavailable');
      } else {
        this.showPostcodeResult('Our team will confirm service availability for ' + postcode + '.', 'pending');
      }
    }
  }

  customElements.define('awa-evap-product-card', AWAEvapProductCard);
})();
